import { ChevronsLeftRightIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'

import { Slider } from '../ui/slider'

type PremiumRangeSliderProps = {
  minPrice: number;
  maxPrice: number;
  allowanceAmount: number;
  value: number;
  onChange: (value: number) => void;
}

export function PremiumRangeSlider ({
  minPrice,
  maxPrice,
  allowanceAmount,
  value,
  onChange,
}: PremiumRangeSliderProps) {
  // We don't need slider if minPrice and maxPrice are the same
  if (minPrice === maxPrice) {
    return null
  }

  const isAllowanceBelowMin = allowanceAmount < minPrice
  const isAllowanceAboveMax = allowanceAmount >= maxPrice

  let minSliderValue = 0
  if (isAllowanceBelowMin) {
    // When allowance is below minimum price,
    // minSliderValue represents the additional amount user needs to pay to reach the minimum plan price
    // on top of the allowance that they will be receiving from the employer.
    minSliderValue = minPrice - allowanceAmount
  }

  let maxSliderValue = Math.round(maxPrice - allowanceAmount)
  if (isAllowanceAboveMax) {
    // When allowance covers all plans, we hide the slider and set maxSliderValue to 0
    // since the user won't need to pay any premium out of pocket
    maxSliderValue = 0
  }

  // Clamp the value to ensure it stays within valid bounds:
  // - Cannot be less than minSliderValue (minimum amount user needs to pay)
  // - Cannot be more than maxSliderValue (maximum amount user can pay)
  // This prevents the slider from going outside the allowed range
  const clampedValue = useMemo(() =>
    Math.max(
      minSliderValue,
      Math.min(value, maxSliderValue)
    ),
  [value, minSliderValue, maxSliderValue]
  )

  // Call onChange when clampedValue is different from the original value
  // This ensures the value stays within bounds when changed from upstream
  useEffect(() => {
    if (clampedValue !== value) {
      onChange(clampedValue)
    }
  }, [])

  const getPlanRangePercent = () => {
    if (isAllowanceAboveMax) {
      // If the allowance is above maximum, we are calculating the width
      // based on the difference between max price & min price, with respect to
      // the difference between allowance & min price.
      return ((maxPrice - minPrice) / (allowanceAmount - minPrice)) * 100
    } else if (allowanceAmount > minPrice) {
      // if the allowance is higher than minimum price, then we render 100%
      return 100
    } else {
      // if the allowance is lower than minimum price, then we are calculating
      // the width based on the difference between maxPrice and minPrice,
      // with respect to the difference between max price and allowance.
      return ((maxPrice - minPrice) / (maxPrice - allowanceAmount)) * 100
    }
  }
  const planRangePercent = getPlanRangePercent()

  const getAllowanceMarkerPercent = () => {
    // If the allowance is below minimum price, then we render at the beginning
    if (isAllowanceBelowMin) {
      return 0

      // If the allowance is above maximum price, then we render at the end, with some
      // buffer to take account of the length of allowance marker
    } else if (isAllowanceAboveMax) {
      return 88
    }

    // If the allowance is between minimum and maximum price, then we locate it
    // based on the difference between allowanec amount and min price,
    // with respect to the difference between max price and min price.
    return ((allowanceAmount - minPrice) / (maxPrice - minPrice)) * 100
  }
  const allowanceMarkerPercent = getAllowanceMarkerPercent()

  // If the allowance is below minimum price, then it should start at where the plan range starts (thus minus it from 100%)
  // otherwise we start where the allowance marker is
  const sliderLeftPercent = isAllowanceBelowMin
    ? 100 - getPlanRangePercent()
    : getAllowanceMarkerPercent()

  return (
    <>
      <div className='w-full mb-6 relative'>
        {/* Range of plan monthly premiums: if the allowance is above maximum, then we don't set marginLeft */}
        <div
          className='bg-gray-100 text-center text-xs leading-4 font-semibold text-gray-800 py-2 border-l border-r border-dashed border-gray-400'
          style={{
            marginLeft: isAllowanceAboveMax
              ? undefined
              : `${100 - planRangePercent}%`,
            width: `${planRangePercent}%`,
          }}
        >
          Range of plan monthly premiums
        </div>

        {/*
          Vertical dashed borders:
          we set the left border just like the plan range above,
          and do the opposite for the right border
        */}
        <div
          className='absolute h-20 border-l border-dashed border-gray-400'
          style={{
            left: isAllowanceAboveMax ? 0 : `${100 - planRangePercent}%`,
          }}
        />
        <div
          className='absolute h-20 border-l border-dashed border-gray-400'
          style={{
            right: isAllowanceAboveMax ? `${100 - planRangePercent}%` : 0,
          }}
        />

        {/*
          Allowance marker (between range bar and slider):
          - we reverse the order of marker and text when we are rendering the allowance at the end of the range.
          - the value of mt and h should be the same of the h of the borders above.
        */}
        <div
          className='absolute mt-6 h-14'
          style={{ left: `${allowanceMarkerPercent}%` }}
        >
          <div
            className={`flex ${
              isAllowanceAboveMax ? 'flex-row-reverse' : 'flex-row'
            } gap-x-2`}
          >
            <div className='flex flex-col items-center'>
              <div className='w-3 h-3 rounded-full bg-white border border-1 border-gray-400' />
              <div className='h-11 border-l border-gray-400 border-dashed' />
            </div>
            <div data-testid='allowance' className='flex flex-col text-center text-xs leading-4 text-gray-800'>
              <div className='font-semibold bg-white'>Your allowance</div>
              <div className='font-normal text-sm bg-white leading-5'>
                ${allowanceAmount.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Black bar (behind the slider or purple marker) */}
        <div className='relative mt-20 h-0.5 bg-gray-800' />

        {/* Purple marker to emphasize the plan premium range, only when the allowance can cover all plans */}
        {isAllowanceAboveMax && (
          <div
            className='absolute h-2 rounded-full bg-[#832ED9]'
            style={{ width: `${planRangePercent}%`, bottom: '-2px' }}
          />
        )}

        {/* Slider, when allowance alone cannot cover all plans */}
        {!isAllowanceAboveMax && (
          <div
            className='absolute'
            style={{
              bottom: '-1.5px',
              left: `${sliderLeftPercent}%`,
              width: `${100 - sliderLeftPercent}%`,
            }}
          >
            <Slider
              value={[clampedValue]}
              min={minSliderValue}
              max={maxSliderValue}
              rangeColor='bg-[#832ED9]'
              trackColor='bg-gray-200'
              thumbStyling='border-none bg-[#832ED9]'
              iconComponent={
                <span className='flex items-center justify-center w-5 h-5'>
                  <ChevronsLeftRightIcon className='text-white w-3 h-3' />
                </span>
              }
              onValueChange={(values) => {
                onChange(values[0])
              }}
              className='w-full'
            />
          </div>
        )}
      </div>

      {/* Plan premium range labels - do the same thing as the range bar border above */}
      <div data-testid='plan-premium-range-labels' className='flex justify-between w-full text-[10px] leading-4 font-normal text-gray-500'>
        <span
          style={{
            marginLeft: isAllowanceAboveMax
              ? undefined
              : `${100 - planRangePercent}%`,
          }}
        >
          ${minPrice.toFixed(0)}
        </span>
        <span
          style={{
            marginRight: isAllowanceAboveMax
              ? `${100 - planRangePercent}%`
              : undefined,
          }}
        >
          ${maxPrice.toFixed(0)}+
        </span>
      </div>
    </>
  )
}
